/**
 * useAppDetection Hook
 * Created: 2025-01-27
 * Updated: 2025-11-28 - Refactored to use regex pattern for flexible environment matching (UAT, PR envs, etc.)
 *
 * Client-side hook for detecting which app (console vs trade) the user is logging into.
 * Uses direct string matching since getConfig() doesn't work reliably in client components.
 * For server components, use is_trade_app() from utils/is-trade-app.util.ts instead.
 */

'use client'

import {useMemo} from 'react'

// Regex pattern to match trade app URLs across all environments:
// - Production: trade.frnt.io
// - Staging: trade.staging.frnt.io
// - UAT: trade.uat.frnt.io
// - PR envs: trade.pr-123.frnt.io, trade.preview-abc.frnt.io, etc.
const TRADE_URL_PATTERN = /trade\.([a-z0-9-]+\.)?frnt\.io/i

/**
 * Hook for detecting app type in client components
 * Returns both isTradeLogin and isConsoleLogin for convenience
 */
export function useAppDetection(redirect_uri: string | null | undefined) {
  const isTradeLogin = useMemo(() => {
    if (!redirect_uri || typeof redirect_uri !== 'string') return false
    // Trade app detection: check for trade URLs across all environments using regex
    // Also supports localhost:5001 for local development
    return TRADE_URL_PATTERN.test(redirect_uri) || redirect_uri.toLowerCase().includes('localhost:5001')
  }, [redirect_uri])

  const isConsoleLogin = useMemo(() => {
    return !isTradeLogin
  }, [isTradeLogin])

  return {
    isTradeLogin,
    isConsoleLogin
  }
}
